import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import api, { fileUrl } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  ArrowLeft, Flag, UserCheck, CheckCircle2, LifeBuoy, MapPin, Loader2, Image as ImageIcon, Settings, Inbox, Mail, Clock,
} from 'lucide-react';
import { toast } from 'sonner';

const STATUS_META = {
  open: { label: 'Open', color: '#C86F53' },
  in_progress: { label: 'In Progress', color: '#D99E4A' },
  needs_support: { label: 'Needs Support', color: '#7A6FC8' },
  resolved: { label: 'Resolved', color: '#7A9A78' },
};
const REASON_LABEL = {
  spam: 'Spam', inappropriate: 'Inappropriate', gone: 'Already gone',
  wrong_location: 'Wrong location', other: 'Other',
};
const TABS = [
  { key: 'open', label: 'Open', countKey: 'open' },
  { key: 'in_progress', label: 'In Progress', countKey: 'in_progress' },
  { key: 'needs_support', label: 'Needs Support', countKey: 'needs_support' },
  { key: 'mine', label: 'Assigned to me', assignment: 'me', countKey: 'mine' },
  { key: 'assigned', label: 'Assigned', assignment: 'others', countKey: 'others' },
  { key: 'resolved', label: 'Resolved', countKey: 'resolved' },
  { key: 'all', label: 'All', countKey: 'all' },
];

function fmt(iso) {
  return new Date(iso).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

export default function ModerationPage({ embedded = false }) {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState('open');
  const tabRef = React.useRef('open');
  useEffect(() => { tabRef.current = tab; }, [tab]);
  const [cases, setCases] = useState([]);
  const [counts, setCounts] = useState({});
  const [busy, setBusy] = useState(false);
  const [resolveCase, setResolveCase] = useState(null);
  const [resolveAction, setResolveAction] = useState('dismiss');
  const [resolveNote, setResolveNote] = useState('');
  const [supportCase, setSupportCase] = useState(null);
  const [supportNote, setSupportNote] = useState('');

  // Support tickets (user-submitted)
  const [view, setView] = useState('reports'); // 'reports' | 'tickets'
  const [tickets, setTickets] = useState([]);
  const [ticketCounts, setTicketCounts] = useState({ active: 0, total: 0 });
  const [ticketFilter, setTicketFilter] = useState('active'); // 'active' | 'all'

  const isStaff = ['moderator', 'admin', 'owner'].includes(user?.role);
  useEffect(() => {
    if (!loading && !isStaff && !embedded) navigate('/');
  }, [isStaff, loading, embedded, navigate]);

  const loadTickets = useCallback(async () => {
    try {
      const [t, k] = await Promise.all([
        api.get('/admin/support-tickets', { params: ticketFilter === 'active' ? { status: 'active' } : {} }),
        api.get('/admin/support-tickets/counts'),
      ]);
      setTickets(t.data);
      setTicketCounts(k.data);
    } catch {
      toast.error('Could not load support tickets');
    }
  }, [ticketFilter]);

  useEffect(() => { if (isStaff && view === 'tickets') loadTickets(); }, [isStaff, view, loadTickets]);
  useEffect(() => { if (isStaff) api.get('/admin/support-tickets/counts').then(r => setTicketCounts(r.data)).catch(() => {}); }, [isStaff]);

  const resolveTicket = async (id) => {
    setBusy(true);
    try { await api.post(`/admin/support-tickets/${id}/resolve`); toast.success('Ticket resolved'); await loadTickets(); }
    catch { toast.error('Could not resolve ticket'); } finally { setBusy(false); }
  };

  const load = useCallback(async () => {
    const reqTab = tab;
    const cfg = TABS.find((t) => t.key === tab) || {};
    const params = cfg.assignment ? { assignment: cfg.assignment } : { status: tab };
    try {
      const [c, k] = await Promise.all([
        api.get('/admin/moderation', { params }),
        api.get('/admin/moderation/counts'),
      ]);
      if (reqTab !== tabRef.current) return; // discard stale response
      setCases(c.data);
      setCounts(k.data);
    } catch {
      toast.error('Could not load moderation queue');
    }
  }, [tab]);

  useEffect(() => { if (isStaff) load(); }, [load, isStaff]);

  const assign = async (cs) => {
    setBusy(true);
    try { await api.post(`/admin/moderation/${cs.id}/assign`); toast.success('Assigned to you'); await load(); }
    catch { toast.error('Could not assign'); } finally { setBusy(false); }
  };

  const submitResolve = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/moderation/${resolveCase.id}/resolve`, { action: resolveAction, note: resolveNote });
      toast.success('Case resolved');
      setResolveCase(null); setResolveNote(''); setResolveAction('dismiss');
      await load();
    } catch { toast.error('Could not resolve'); } finally { setBusy(false); }
  };

  const submitSupport = async () => {
    if (!supportNote.trim()) { toast.error('Add a note for support'); return; }
    setBusy(true);
    try {
      await api.post(`/admin/moderation/${supportCase.id}/support`, { note: supportNote });
      toast.success('Marked as needs support');
      setSupportCase(null); setSupportNote('');
      await load();
    } catch { toast.error('Could not update'); } finally { setBusy(false); }
  };

  if (loading || !isStaff) {
    return <div className="h-screen flex items-center justify-center text-brand-ink">Loading…</div>;
  }

  return (
    <div className={embedded ? '' : 'min-h-screen bg-brand-paper'} data-testid="moderation-page">
      <div className={embedded ? '' : 'max-w-4xl mx-auto p-6 sm:p-10'}>
        {!embedded && (
          <div className="flex items-center justify-between mb-6">
            <Link to="/" className="inline-flex items-center gap-2 text-brand-slate hover:text-brand-ink" data-testid="back-to-map">
              <ArrowLeft className="w-4 h-4" /> Back to map
            </Link>
            <Link to="/admin" className="inline-flex items-center gap-2 text-brand-slate hover:text-brand-ink" data-testid="to-site-content">
              <Settings className="w-4 h-4" /> Admin
            </Link>
          </div>
        )}

        <h1 className="font-display font-black text-4xl tracking-tight text-brand-ink mb-2 flex items-center gap-3">
          <Flag className="w-8 h-8 text-[#C86F53]" /> Moderation Queue
        </h1>
        <p className="text-brand-slate mb-6">Review reported pins and user support tickets. Assign a case to yourself, then resolve or request support.</p>

        {/* View switch */}
        <div className="flex gap-2 mb-5">
          <button
            data-testid="mod-view-reports"
            onClick={() => setView('reports')}
            className={`rounded-full px-4 py-2 text-sm font-semibold transition inline-flex items-center gap-2 ${view === 'reports' ? 'bg-brand-ink text-white' : 'glass-panel text-brand-ink hover:bg-white'}`}
          >
            <Flag className="w-4 h-4" /> Reported Pins
          </button>
          <button
            data-testid="mod-view-tickets"
            onClick={() => setView('tickets')}
            className={`rounded-full px-4 py-2 text-sm font-semibold transition inline-flex items-center gap-2 ${view === 'tickets' ? 'bg-brand-ink text-white' : 'glass-panel text-brand-ink hover:bg-white'}`}
          >
            <LifeBuoy className="w-4 h-4" /> Support Tickets
            <span className="opacity-80 bg-brand text-white rounded-full text-xs px-1.5 min-w-[18px] text-center">{ticketCounts.active ?? 0}</span>
          </button>
        </div>

        {view === 'tickets' ? (
          <div data-testid="support-tickets-view">
            <div className="flex gap-2 mb-6">
              {['active', 'all'].map(f => (
                <button
                  key={f}
                  data-testid={`ticket-filter-${f}`}
                  onClick={() => setTicketFilter(f)}
                  className={`rounded-full px-4 py-2 text-sm font-medium transition ${ticketFilter === f ? 'bg-brand text-white' : 'glass-panel text-brand-ink hover:bg-white'}`}
                >
                  {f === 'active' ? 'Active' : 'All'}
                  <span className="ml-1.5 opacity-70">{f === 'active' ? (ticketCounts.active ?? 0) : (ticketCounts.total ?? 0)}</span>
                </button>
              ))}
            </div>
            {tickets.length === 0 ? (
              <div className="glass-panel rounded-3xl p-10 text-center text-brand-slate" data-testid="tickets-empty">
                <Inbox className="w-10 h-10 mx-auto mb-3 text-[#7A9A78]" />
                No support tickets here.
              </div>
            ) : (
              <div className="space-y-4">
                {tickets.map((t) => {
                  const sm = STATUS_META[t.status] || STATUS_META.open;
                  return (
                    <div key={t.id} className="glass-panel rounded-3xl p-5 sm:p-6" data-testid={`ticket-${t.id}`}>
                      <div className="flex items-center gap-2 flex-wrap mb-2">
                        <h3 className="font-display font-bold text-lg text-brand-ink" data-testid="ticket-subject">{t.subject}</h3>
                        <Badge style={{ background: sm.color + '22', color: sm.color }} className="border-0">{sm.label}</Badge>
                        <span className="text-xs text-brand-slate ml-auto flex items-center gap-1"><Clock className="w-3 h-3" /> {fmt(t.created_at)}</span>
                      </div>
                      <p className="text-sm text-brand-slate whitespace-pre-wrap mb-3">{t.message}</p>
                      <div className="flex items-center gap-3 flex-wrap text-xs text-brand-slate border-t border-black/5 pt-3">
                        <span className="font-semibold text-brand-ink">@{t.username || 'user'}</span>
                        <a href={`mailto:${t.email}`} className="inline-flex items-center gap-1 text-brand hover:underline"><Mail className="w-3.5 h-3.5" /> {t.email}</a>
                        {t.resolved_by && <span className="text-[#7A9A78]">Resolved by @{t.resolved_by}</span>}
                        {t.status !== 'resolved' && (
                          <Button data-testid={`ticket-resolve-${t.id}`} size="sm" disabled={busy} onClick={() => resolveTicket(t.id)} className="ml-auto rounded-full bg-brand hover:bg-brand-hover text-white">
                            <CheckCircle2 className="w-4 h-4 mr-1" /> Mark resolved
                          </Button>
                        )}
                        {t.status === 'resolved' && (
                          <a href={`mailto:${t.email}?subject=Re: ${encodeURIComponent(t.subject)}`} className="ml-auto">
                            <Button size="sm" variant="ghost" className="rounded-full text-brand"><Mail className="w-4 h-4 mr-1" /> Reply</Button>
                          </a>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        ) : (
        <>
        <div className="flex flex-wrap gap-2 mb-6">
          {TABS.map((t) => (
            <button
              key={t.key}
              data-testid={`mod-tab-${t.key}`}
              onClick={() => setTab(t.key)}
              className={`rounded-full px-4 py-2 text-sm font-medium transition ${tab === t.key ? 'bg-brand text-white' : 'glass-panel text-brand-ink hover:bg-white'}`}
            >
              {t.label}
              <span className="ml-1.5 opacity-70">{counts[t.countKey] ?? 0}</span>
            </button>
          ))}
        </div>

        {cases.length === 0 ? (
          <div className="glass-panel rounded-3xl p-10 text-center text-brand-slate" data-testid="mod-empty">
            <CheckCircle2 className="w-10 h-10 mx-auto mb-3 text-[#7A9A78]" />
            Nothing here. The neighborhood is tidy.
          </div>
        ) : (
          <div className="space-y-4">
            {cases.map((cs) => {
              const sm = STATUS_META[cs.status] || STATUS_META.open;
              const mineUnassigned = cs.status !== 'resolved';
              return (
                <div key={cs.id} className="glass-panel rounded-3xl p-5 sm:p-6" data-testid={`mod-case-${cs.id}`}>
                  <div className="flex gap-4">
                    <div className="w-20 h-20 rounded-2xl overflow-hidden bg-black/5 flex items-center justify-center shrink-0">
                      {cs.pin?.photos?.[0] ? (
                        <img src={fileUrl(cs.pin.photos[0])} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <ImageIcon className="w-6 h-6 text-brand-dim" />
                      )}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="font-display font-bold text-lg text-brand-ink truncate" data-testid="mod-case-title">
                          {cs.pin?.title || '(pin deleted)'}
                        </h3>
                        <Badge style={{ background: sm.color + '22', color: sm.color }} className="border-0" data-testid="mod-case-status">
                          {sm.label}
                        </Badge>
                        <span className="text-xs text-brand-slate">· {cs.report_count} report{cs.report_count === 1 ? '' : 's'}</span>
                      </div>
                      <div className="flex flex-wrap gap-1.5 mt-2">
                        {(cs.reasons || []).map((r) => (
                          <span key={r} className="text-[11px] px-2 py-0.5 rounded-full bg-black/5 text-brand-slate">{REASON_LABEL[r] || r}</span>
                        ))}
                      </div>
                      {cs.assigned_to_name && (
                        <div className="text-xs text-brand-slate mt-2 flex items-center gap-1">
                          <UserCheck className="w-3.5 h-3.5" /> Assigned to @{cs.assigned_to_name}
                        </div>
                      )}
                      {cs.support_note && cs.status === 'needs_support' && (
                        <div className="text-xs text-[#7A6FC8] mt-1">Support requested: {cs.support_note}</div>
                      )}
                      {cs.resolution && cs.status === 'resolved' && (
                        <div className="text-xs text-[#7A9A78] mt-1">Resolution: {cs.resolution}</div>
                      )}
                    </div>
                  </div>

                  {cs.reports?.length > 0 && (
                    <div className="mt-4 pt-3 border-t border-black/5 space-y-1.5">
                      {cs.reports.map((rp, i) => (
                        <div key={rp.reporter_id || `${rp.reason}-${i}`} className="text-xs text-brand-slate flex gap-2">
                          <span className="font-semibold text-brand-ink">{REASON_LABEL[rp.reason] || rp.reason}</span>
                          {rp.note && <span>— {rp.note}</span>}
                          <span className="ml-auto whitespace-nowrap opacity-70">@{rp.reporter} · {fmt(rp.created_at)}</span>
                        </div>
                      ))}
                    </div>
                  )}

                  <div className="mt-4 flex flex-wrap gap-2">
                    {mineUnassigned && (
                      <Button data-testid="mod-assign-btn" size="sm" disabled={busy} onClick={() => assign(cs)} variant="outline" className="rounded-full">
                        <UserCheck className="w-4 h-4 mr-1" /> Assign to me
                      </Button>
                    )}
                    {cs.status !== 'resolved' && (
                      <Button data-testid="mod-resolve-btn" size="sm" disabled={busy} onClick={() => { setResolveCase(cs); setResolveAction('dismiss'); }} className="rounded-full bg-brand hover:bg-brand-hover text-white">
                        <CheckCircle2 className="w-4 h-4 mr-1" /> Resolve
                      </Button>
                    )}
                    {cs.status !== 'resolved' && (
                      <Button data-testid="mod-support-btn" size="sm" disabled={busy} onClick={() => setSupportCase(cs)} variant="outline" className="rounded-full">
                        <LifeBuoy className="w-4 h-4 mr-1" /> Request support
                      </Button>
                    )}
                    {cs.pin && (
                      <a href={`/?pin=${cs.pin.id}`} target="_blank" rel="noopener noreferrer">
                        <Button size="sm" variant="ghost" className="rounded-full text-brand" data-testid="mod-view-btn">
                          <MapPin className="w-4 h-4 mr-1" /> View on map
                        </Button>
                      </a>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
        </>
        )}
      </div>

      {/* Resolve dialog */}
      <Dialog open={!!resolveCase} onOpenChange={(o) => !o && setResolveCase(null)}>
        <DialogContent className="rounded-3xl" data-testid="resolve-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">Resolve case</DialogTitle>
            <DialogDescription>Choose what to do with "{resolveCase?.pin?.title || 'this pin'}".</DialogDescription>
          </DialogHeader>
          <Select value={resolveAction} onValueChange={setResolveAction}>
            <SelectTrigger className="rounded-xl" data-testid="resolve-action-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="dismiss" data-testid="resolve-opt-dismiss">Dismiss reports (keep pin)</SelectItem>
              <SelectItem value="hide" data-testid="resolve-opt-hide">Hide pin</SelectItem>
              <SelectItem value="delete" data-testid="resolve-opt-delete">Delete pin permanently</SelectItem>
            </SelectContent>
          </Select>
          <Textarea data-testid="resolve-note" value={resolveNote} onChange={(e) => setResolveNote(e.target.value)} placeholder="Resolution note (optional)" className="rounded-xl min-h-[70px]" />
          <DialogFooter>
            <Button variant="ghost" className="rounded-full" onClick={() => setResolveCase(null)}>Cancel</Button>
            <Button data-testid="resolve-confirm-btn" disabled={busy} onClick={submitResolve} className="rounded-full bg-brand hover:bg-brand-hover text-white">
              {busy ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <CheckCircle2 className="w-4 h-4 mr-1" />} Confirm
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Support dialog */}
      <Dialog open={!!supportCase} onOpenChange={(o) => !o && setSupportCase(null)}>
        <DialogContent className="rounded-3xl" data-testid="support-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">Request additional support</DialogTitle>
            <DialogDescription>Flag this case for another admin or platform support.</DialogDescription>
          </DialogHeader>
          <Textarea data-testid="support-note" value={supportNote} onChange={(e) => setSupportNote(e.target.value)} placeholder="Describe what you need help with…" className="rounded-xl min-h-[90px]" />
          <DialogFooter>
            <Button variant="ghost" className="rounded-full" onClick={() => setSupportCase(null)}>Cancel</Button>
            <Button data-testid="support-confirm-btn" disabled={busy} onClick={submitSupport} className="rounded-full bg-[#7A6FC8] hover:bg-[#6a5fb0] text-white">
              {busy ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <LifeBuoy className="w-4 h-4 mr-1" />} Submit
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
